import { Button, Dialog, Form, Icon, Checkbox } from '@hai/ui-react';
import { FileUploadStages, IUploadState } from '@hai/ui-react/dist/types';
import { isEmpty, isNil } from 'ramda';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath } from 'src/js/api/route-path-index';
import { UploadZone } from 'src/js/component/base/upload-zone';
import { NullifierNetworkErrorHandler } from 'src/js/controller/network-error-handler';
import { triggerEvent } from 'src/js/events';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { useErrorReaction, useSuccessReaction } from 'src/js/hook/use-reaction';
import { useTask } from 'src/js/hook/use-task';
import { DeleteFirmwareResponse } from 'src/js/model/api/response/delete-firmware-response';
import {
  MonitorFirmwareUploadResponse,
  MonitorFirmwareUploadState,
} from 'src/js/model/api/response/monitor-firmware-upload-response';
import { RebootResponse } from 'src/js/model/api/response/reboot-response';
import { UploadFirmwareResponse } from 'src/js/model/api/response/upload-firmware-response';
import { MakitoPaths } from 'src/js/route';
import { createRebootTask } from 'src/js/task/settings-tasks';
import { createDeleteFirmwareTask, createMonitorFirmwareUploadTask } from 'src/js/task/settings-upgrade-tasks';
import { constant } from 'src/js/constant';

import { UpdateCancelConfirmModal } from './cancel-confirm-modal';
import { CurrentVersionViewModel } from './current-version-view-model';
import { generateDataAuto } from 'src/js/util/automation';
import { useMutable } from 'src/js/hook/use-mutable';
import loginBackgroundImage from 'src/images/login-bg.jpg';
import { ApiResponse, CancelToken } from 'apisauce';
import { CancelTokenSource } from 'axios';

interface CompleteProps {
  uploadStep: MonitorFirmwareUploadState;
  packageReady: boolean;
  fileUploaded: boolean;
  errorMessage: string;
  autoReboot: boolean;
}

// TODO move to its own file
const UploadComplete = (props: CompleteProps) => {
  const { uploadStep, packageReady, fileUploaded, errorMessage, autoReboot } = props;
  const { t } = useTranslation();

  return (
    <div
      className={`upload-complete-container d-flex flex-column align-items-center justify-content-center ${
        errorMessage || fileUploaded || packageReady ? 'last-step' : ''
      }`}
    >
      {fileUploaded || packageReady ? (
        <Icon iconname={'StatusOK'} size="md" />
      ) : uploadStep === MonitorFirmwareUploadState.ERROR ? (
        <Icon iconname={'StatusError'} size="md" />
      ) : (
        <Icon
          className={constant.isMissionCritical ? 'aqua-svg' : 'purple-svg'}
          iconname={'StatusLoading'}
          size="md"
          spinning={true}
        />
      )}
      <div className="mk-upload-zone-text haiui-body-02 hai-gray-08 hai-mt-2">
        {fileUploaded || packageReady
          ? autoReboot
            ? t('settings.upgrade.modal.autoReboot')
            : t('settings.upgrade.modal.reboot')
          : uploadStep === MonitorFirmwareUploadState.ERROR
          ? errorMessage
          : t(`settings.upgrade.modal.uploadingSubTitle.step${uploadStep}`)}
      </div>
    </div>
  );
};

interface Props {
  data: CurrentVersionViewModel;
  onCancel: VoidFunction;
  /** */
  packageReady: boolean;
  setPackageReady: React.Dispatch<React.SetStateAction<boolean>>;
  /** */
  fileUploaded: boolean;
  setFileUploaded: React.Dispatch<React.SetStateAction<boolean>>;
}

const prefetchLoginBackgroundImage = () => {
  const image = new Image();
  image.src = loginBackgroundImage;
};

// https://axios-http.com/docs/cancellation
// let controller = new AbortController(); // axios 0.22+
// let source = CancelToken.source(); // axios 0.21 and less
let source: CancelTokenSource = null; // axios 0.21 and less
export const UpdateSoftwareModal = (props: Props) => {
  const { data, onCancel, packageReady, setPackageReady, fileUploaded, setFileUploaded } = props;
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [file, setFile] = useState<File>(undefined);
  const [cancelShown, showCancel, hideCancel] = useModalControls(false);
  const [startUpload, setStartUpload] = useState<boolean>(false);
  const [rebootAfterUpload, setRebootAfterUpload] = useState<boolean>(false);
  const [rebootAfterUploadMutable, setRebootAfterUploadMutable] = useMutable<boolean>(false);
  const [uploadState, setUploadState] = useState<IUploadState>({
    stage: FileUploadStages.NONE,
    progress: 0,
  });

  const rebootTask = useTask(createRebootTask());
  const cancelTask = useTask(createDeleteFirmwareTask());

  useSuccessReaction(rebootTask, (_result: RebootResponse) => {
    navigate(MakitoPaths.rebootingUpgrade);
  });

  useSuccessReaction(cancelTask, (_result: DeleteFirmwareResponse) => {
    setFile(undefined);
    setFileUploaded(false);
    setPackageReady(false);
    setStartUpload(false);
    setUploadState({ stage: FileUploadStages.NONE, progress: 0 });
  });

  const handleOnCancel = async () => {
    if (!isNil(file)) {
      cancelTask(file.name);
      setFileUploaded(false);
      setPackageReady(false);
    }

    onCancel(); // close the modal so user dont see any error while we cancel

    // this stop the frontend POST upload
    // controller.abort();
    source?.cancel('abort message');

    // this restarts encoders
    await apiClientWithoutHandler.genericController.post(APIPath.upgrade.cancel, {}).then((_res: any) => {
      // console.log('cancel success')
    });

    triggerEvent('enable-preset-fetch');
  };

  const handleOnReboot = (): void => {
    rebootTask();
  };

  const handleOnRebootLater = (): void => {
    setFileUploaded(false);
    setPackageReady(false);
    if (!isNil(file)) {
      data.packageToBeInstalled = file.name;
    }
    onCancel();
  };

  const [monitoring, setMonitoring] = useState<boolean>(true);
  const [uploadStep, setUploadStep] = useState<MonitorFirmwareUploadState>(MonitorFirmwareUploadState.UPLOADING);
  const [realtimeUploadStep, setRealtimeUploadStep] = useMutable<MonitorFirmwareUploadState>(
    MonitorFirmwareUploadState.UPLOADING,
  ); // NKE-3104 & NKE-3105

  // We don't want to handle the errors here because the backend returns 403 at the end of the upload but it's still valid...
  const monitorUploadTask = useTask(createMonitorFirmwareUploadTask(undefined, new NullifierNetworkErrorHandler()));

  const handleOnRetry = () => {
    if (!isNil(file)) {
      cancelTask(file?.name);
      setFileUploaded(false);
      setFile(undefined);
      setErrorMessage(undefined);
      setUploadState({
        stage: FileUploadStages.NONE,
        progress: 0,
      });
      setUploadStep(MonitorFirmwareUploadState.UPLOADING);
      setRealtimeUploadStep(MonitorFirmwareUploadState.UPLOADING);
    }
  };

  const onFinalizeSuccessfulUpload = () => {
    prefetchLoginBackgroundImage();
    if (rebootAfterUploadMutable()) {
      // auto reboot
      setTimeout(() => {
        rebootTask();
      }, 1000);
      return; // returning early skip the upload ready green checkmark screen
    }
    setFileUploaded(true);
    setUploadState((prev: IUploadState) => {
      return { ...prev, stage: FileUploadStages.COMPLETED };
    });
    if (!rebootAfterUploadMutable()) {
      triggerEvent('enable-preset-fetch');
    }
  };

  const [errorMessage, setErrorMessage] = useState<string>();
  const onErrorUpload = (message?: string) => {
    triggerEvent('enable-preset-fetch');
    setStartUpload(false);
    setUploadStep(MonitorFirmwareUploadState.ERROR);
    setRealtimeUploadStep(MonitorFirmwareUploadState.ERROR);
    const prefix = 'Error: ';
    if (message?.startsWith(prefix)) {
      message = message.substring(prefix.length);
    }
    setErrorMessage(message ?? 'There might be a connection problem. Please reboot and retry uploading.');
  };

  useSuccessReaction(
    monitorUploadTask,
    (result: MonitorFirmwareUploadResponse) => {
      if (realtimeUploadStep() === MonitorFirmwareUploadState.ERROR) {
        // lets end here, otherwise NKE-3104
        return;
      }

      if (
        result.state === MonitorFirmwareUploadState.UPLOADING ||
        result.state === MonitorFirmwareUploadState.UNPACKING
      ) {
        // state 1 copying
        // state 2 verifying
        setUploadStep(MonitorFirmwareUploadState.UNPACKING);
        setRealtimeUploadStep(MonitorFirmwareUploadState.UNPACKING);
      } else if (result.state === MonitorFirmwareUploadState.SYNCING) {
        console.log('update state 3');
        setUploadStep(result.state);
        setRealtimeUploadStep(result.state);
        setMonitoring(false);
      }

      if (monitoring) {
        setTimeout(() => {
          monitorUploadTask(file.name);
        }, 1000);
      }
    },
    {},
    [file, monitoring],
  );

  useErrorReaction(
    monitorUploadTask,
    () => {
      // maybe the file was not ready, just retry
      if (monitoring) {
        setTimeout(() => {
          monitorUploadTask(file.name);
        }, 1000);
      }
    },
    {},
    [file, monitoring],
  );

  const onSuccessUpload = (result: ApiResponse<UploadFirmwareResponse>) => {
    // console.log('on success upload', result)
    if (result.data?.error) {
      onErrorUpload(result.data?.message);
      setMonitoring(false);
    } else {
      // go to step 2
      setUploadStep(MonitorFirmwareUploadState.UNPACKING);
      setRealtimeUploadStep(MonitorFirmwareUploadState.UNPACKING);
      setUploadState((prev: IUploadState) => {
        return { ...prev, stage: FileUploadStages.INPROGRESS, progress: 100 };
      });

      console.log('going to POST install');

      const path = APIPath.upgrade.install;
      apiClientWithoutHandler.genericController.post(path).then((res: any) => {
        setMonitoring(false);

        console.log('POST install result', res.data, res);
        if (res.ok) {
          if (res.data?.error) {
            onErrorUpload(res.data?.message ?? t('settings.upgrade.modal.failedInstallStep'));
            return res;
          }
          // success
          onFinalizeSuccessfulUpload();
        } else {
          // FIXME this case seems to not work
          onErrorUpload(res.data?.message ?? t('settings.upgrade.modal.failedInstallStep'));
        }
        return res;
      });
    }
  };

  const handleFileChange = (files: File[]) => {
    setFile(isEmpty(files) ? undefined : files[0]);
    return false; // handled
  };

  const handleUploadFile = () => {
    triggerEvent('stop-preset-fetch');
    setStartUpload(true);
    setUploadStep(MonitorFirmwareUploadState.UPLOADING);
    setRealtimeUploadStep(MonitorFirmwareUploadState.UPLOADING);
    setMonitoring(true);

    apiClientWithoutHandler.genericController.post(APIPath.upgrade.notify, {}).then((_res: any) => {
      source = CancelToken.source();
      apiClientWithoutHandler.genericController
        .upload<UploadFirmwareResponse>(
          APIPath.upgrade.upload,
          { file: file },
          { cancelToken: source.token },
          (e: any) => {
            const toProgress = (e.loaded * 100) / e.total;
            // console.log('upload progress', e);
            setUploadState((prev: IUploadState) => {
              return {
                ...prev,
                stage: FileUploadStages.INPROGRESS,
                progress: Math.round(toProgress),
              };
            });
            if (e.loaded === e.total) {
              setUploadStep(MonitorFirmwareUploadState.UNPACKING);
              setRealtimeUploadStep(MonitorFirmwareUploadState.UNPACKING);
            }
          },
        )
        .then((res) => {
          if (res.ok) {
            onSuccessUpload(res);
          } else {
            onErrorUpload();
          }
        });
    });

    // give some time for the file to be there
    setTimeout(() => {
      monitorUploadTask(file.name);
    }, 1000);
  };

  const getHint = () => {
    switch (uploadState.stage) {
      case FileUploadStages.NONE:
        return packageReady ? getSuccessMessage() : t('settings.upgrade.modal.uploadHai');

      case FileUploadStages.INPROGRESS:
        return t(`settings.upgrade.modal.uploadingTitle.step${uploadStep}`, {
          fileName: file.name,
        });

      case FileUploadStages.COMPLETED:
        return getSuccessMessage();
    }
    return '';
  };

  const getSuccessMessage = () => {
    return !isNil(data.packageToBeInstalled) || file?.name
      ? t('settings.upgrade.modal.uploadCompleted', {
          fileName: data.packageToBeInstalled || file.name,
        })
      : '';
  };

  const getUploadNotice = () => {
    const message = startUpload
      ? uploadState.stage === FileUploadStages.NONE
        ? t('settings.upgrade.modal.uploadNotice.stopping')
        : ''
      : t('settings.upgrade.modal.uploadNotice.willStop');

    return !isEmpty(message) ? (
      <>
        <div>
          <Icon iconname="StatusWarning" className="hai-mr-3" size="sm2" />
        </div>
        <div className="haiui-body-03 hai-gray-08" data-auto={generateDataAuto(`update`, `message`)}>
          {message}
        </div>
      </>
    ) : (
      <></>
    );
  };

  // console.log('render', errorMessage, uploadStep, fileUploaded)
  const content = (): ReactNode => {
    return (
      <Form>
        <>
          <div className="haiui-label-01-med hai-gray-08 hai-mb-4">{getHint()}</div>
          {uploadStep === MonitorFirmwareUploadState.UPLOADING && !fileUploaded ? (
            <UploadZone
              accept={{ '': constant.firmware.fileTypes }}
              browseMessage={packageReady ? '' : undefined}
              conjunction={packageReady ? '' : undefined}
              file={file}
              icon="UpdateSoftware"
              mainMessageAfterSelection={t('upload.mainMessageAfterSelection')}
              uploadState={uploadState}
              onFileChange={handleFileChange}
            />
          ) : (
            <UploadComplete
              uploadStep={uploadStep}
              packageReady={packageReady}
              fileUploaded={fileUploaded}
              errorMessage={errorMessage}
              autoReboot={rebootAfterUpload}
            />
          )}
          {!fileUploaded && !errorMessage && (
            <div className="d-flex hai-mt-6 mk-light-font">
              <Checkbox
                checked={rebootAfterUpload}
                onChange={(e: any) => {
                  const { checked } = e.target;
                  setRebootAfterUpload(checked);
                  setRebootAfterUploadMutable(checked);
                }}
              >
                {t('upload.rebootAfterUploadCheckbox')}
              </Checkbox>
            </div>
          )}
          <div className="d-flex hai-mt-3">
            {packageReady || fileUploaded ? (
              <div className="d-flex flex-equal justify-content-between hai-mt-4">
                <div className="d-flex align-items-center align-items-center">
                  <Button className="hai-mr-6" variant="ghost" onClick={showCancel} disabled={rebootAfterUpload}>
                    {t('general.cancel')}
                  </Button>
                </div>
                <div className="d-flex justify-content-end">
                  <Button
                    className="hai-mr-6"
                    variant="secondary"
                    onClick={handleOnRebootLater}
                    disabled={rebootAfterUpload}
                  >
                    {t('settings.upgrade.modal.rebootLater')}
                  </Button>
                  <Button variant="primary" onClick={handleOnReboot} disabled={rebootAfterUpload}>
                    {t('general.reboot')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex flex-equal justify-content-between hai-mt-4">
                  <div className="d-flex flex-equal align-items-center align-items-center">{getUploadNotice()}</div>
                  <div className="d-flex flex-equal justify-content-end">
                    <Button
                      className="hai-mr-6"
                      variant="secondary"
                      onClick={handleOnCancel}
                      disabled={
                        uploadStep === MonitorFirmwareUploadState.UNPACKING ||
                        uploadStep === MonitorFirmwareUploadState.SYNCING
                      }
                    >
                      {t('general.cancel')}
                    </Button>
                    {uploadStep === MonitorFirmwareUploadState.ERROR ? (
                      <Button variant="primary" onClick={handleOnRetry}>
                        {t('general.retry')}
                      </Button>
                    ) : (
                      <Button variant="primary" onClick={handleUploadFile} disabled={isNil(file) || startUpload}>
                        {t('general.upload')}
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          {cancelShown && (
            <UpdateCancelConfirmModal fileName={file.name} onCancel={hideCancel} onApply={handleOnCancel} />
          )}
        </>
      </Form>
    );
  };

  return (
    <Dialog
      className="update-software-modal"
      accentColor={constant.productColor}
      title={t('settings.upgrade.modal.title')}
      show={true}
      dialogType="activity"
      headerIcon="UpdateSoftware"
      size="lg"
      content={content()}
    />
  );
};
